import type {
  ContentBlock,
  Page,
} from '~~/@types/generated/cms/graphql-schema-types'

import { useState } from '#app'

import {
  AcknowledgementPage,
  AcknowledgementState,
} from '~/features/Acknowledgement/types'

export const useAcknowledgement = () => {
  const showAcknowledgementModal = useState(
    AcknowledgementState.showAcknowledgementModal,
    () => false,
  )

  const { isLoggedIn } = useAuth()
  const { log } = useDatadog()
  const { getPage } = useContent()

  const getAcknowledgementDate = (data: Page) => {
    const dateString = (data?.contentBlocksCollection?.items[0] as ContentBlock)
      ?.acknowledgmentDate

    return new Date(dateString || '')
  }

  const checkAcknowledgementStatus = async () => {
    if (!isLoggedIn.value) return

    // SF-5671: When lazy is set to true, the first request to privacy-policy is blocked
    const getPageOptions = {
      lazy: false,
    }

    const { data: privacyPolicyData } = await getPage(
      AcknowledgementPage.PrivacyPolicy,
      getPageOptions,
    )
    const { data: termsAndConditionsData } = await getPage(
      AcknowledgementPage.TermsAndConditions,
      getPageOptions,
    )

    const acknowledgementDatePrivacyPolicy = getAcknowledgementDate(
      privacyPolicyData.value as Page,
    )
    const acknowledgementDateTermsAndConditions = getAcknowledgementDate(
      termsAndConditionsData.value as Page,
    )

    const acknowledgementDates = [
      acknowledgementDatePrivacyPolicy.getTime(),
      acknowledgementDateTermsAndConditions.getTime(),
    ].filter((d) => !isNaN(d))

    const latestAcknowledgementDate = Math.max(...acknowledgementDates)

    try {
      const { load: fetchUser } = useUserDataLazyQuery({
        fetchPolicy: 'cache-first',
      })
      const user = await fetchUser()
      if (!user) return

      const lastAcknowledgedTermsAndConditionsDateString =
        user?.player?.lastAcknowledgedTermsAndConditions

      const lastAcknowledgedTermsAndConditionsDate =
        lastAcknowledgedTermsAndConditionsDateString
          ? new Date(lastAcknowledgedTermsAndConditionsDateString).getTime()
          : null

      if (
        lastAcknowledgedTermsAndConditionsDate &&
        latestAcknowledgementDate > lastAcknowledgedTermsAndConditionsDate
      ) {
        showAcknowledgementModal.value = true
      }
    } catch (error) {
      log('error', {
        component: 'features/composables/acknowledgement.ts',
        message: 'Fetching user data for acknowledgement check failed',
        messageContext: { error },
      })
    }
  }

  return {
    checkAcknowledgementStatus,
    showAcknowledgementModal,
  }
}
